import AddLinkIcon from '@mui/icons-material/AddLink';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import DeleteIconButton from '../../components/DeleteIconButton';
import DescriptionIcon from '@mui/icons-material/Description';
// import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import FileCopyOutlinedIcon from '@mui/icons-material/FileCopyOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Box, Divider, FormControl, Grid, IconButton, InputLabel, Select, Tooltip, Typography } from '@mui/material';
import { grey } from '@mui/material/colors';
import { makeStyles } from '@mui/styles';
// import { throttle } from 'lodash';
import { useSnackbar } from 'notistack';
import React from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { useDropzone } from 'react-dropzone';

import { guessPriceDisplay, parsePriceDisplay, priceDisplayHtmlOptions } from '../../activity/utils';
import { DataAPIContext, GET_LIST } from '../../api';
import { DELETE, UPDATE, UPLOAD } from '../../api/dataProvider';
import FixedLinearProgress from '../../components/FixedLinearProgress';
import StyledRemoteAutocomplete from '../../components/StyledRemoteAutocomplete';
import { checkAndAddHttpPrefix } from '../../utils';
import fileToSha1String from '../../utils/file_to_sha1';
import { validateTripActivity } from '../../utils/validators/trip';
import { getCurrencyFromActivities } from '../utils';
import PriceDisplay from './ActivityPriceDisplay';
import { ActivityDocumentsDialog, CustomOntripDocumentList } from './TripEditCustomOntripDocument';

const DND_ITEM = 'activity_item';

const useStyles = makeStyles((theme) => ({
    root: {
        padding: '0.5rem 0',
        paddingLeft: '57px',
        [theme.breakpoints.down('sm')]: {
            padding: '1rem 0',
        },
    },
    arrowButton: {
        width: '25px',
        height: '25px',
        display: 'flex',
        justifyContent: 'center',
        border: `solid 1px ${grey[400]}`,
        borderRadius: '5px',
        cursor: 'pointer',
        background: 'transparent',
        color: theme.palette.primary.main,
        alignItems: 'center',
    },
    dragHandle: {
        cursor: 'grab',
    },
    divider: {
        paddingBottom: '0rem',
        paddingTop: '1rem',
        paddingRight: '2rem',
        [theme.breakpoints.down('sm')]: {
            padding: 0,
            paddingTop: '1rem',
        },
    },
}));

const FILE = 1;
const LINK = 2;

const emptyLink = {
    deep_link: false,
    link: '',
    name: '',
    sequence: 0,
    type: LINK,
    url: '',
};

const normalizeOntripDocumentForCompare = (doc) => {
    const isActivityRelation = !!doc?.document;
    const d = isActivityRelation ? doc.document : doc;

    return {
        type: d?.type,
        content_hash: d?.content_hash || null,
        url: d?.type === LINK ? (checkAndAddHttpPrefix(d?.url || d?.link) || '').toLowerCase() : null,
        name: (d?.name || '').trim().toLowerCase(),
    };
};

export const areOntripDocumentsDuplicate = (docA, docB) => {
    const a = normalizeOntripDocumentForCompare(docA);
    const b = normalizeOntripDocumentForCompare(docB);

    if (!a.type || a.type !== b.type) {
        return false;
    }

    if (a.content_hash && b.content_hash && a.content_hash === b.content_hash) {
        return true;
    }

    if (a.type === LINK && a.url && b.url && a.url === b.url) {
        return true;
    }

    if (a.type === FILE && a.name && b.name && a.name === b.name) {
        return true;
    }

    return false;
};

const isDuplicateDocumentToCopy = (candidate, existingDocs, selectedDocs) => {
    return [...(existingDocs || []), ...(selectedDocs || [])].some((other) => areOntripDocumentsDuplicate(candidate, other));
};

const filterDuplicateDocumentsToCopy = (selectedDocs, existingDocs, replace) => {
    const accepted = [];

    selectedDocs.forEach((doc) => {
        if (isDuplicateDocumentToCopy(doc, replace ? [] : existingDocs, accepted)) {
            return;
        }
        accepted.push(doc);
    });

    return accepted;
};

export const TripFlowActivity = (props) => {
    const classes = useStyles();
    const { enqueueSnackbar } = useSnackbar();
    const priceDisplay = guessPriceDisplay(props.activity);

    //Documents management
    const resource = 'tour_activity';
    const dataAPI = React.useContext(DataAPIContext);
    const [docs, setDocs] = React.useState(props.activity.documents || []);
    const [syncActive, setSyncActive] = React.useState(null);
    const [docsToCopy, setDocsToCopy] = React.useState(null);
    const defaultCurrency = getCurrencyFromActivities(props.trip);

    const onChange = (newActivity) => {
        const { canSave, errors } = validateTripActivity({ ...props.activity, ...newActivity });
        props.onChange && props.onChange(newActivity, props.idx);
        props.onValidate && props.onValidate(`activities[${props.idx}]`, canSave, errors);
    };

    const onActivityChange = (ev, nv) => {
        let current_activity_id = props.activity.id;
        onChange({
            // ...props.activity,
            _changed_activity: true,
            activity: nv,
            price_display: guessPriceDisplay(nv),
            cost_prefix: nv?.cost_prefix,
            cost_unit: nv?.cost_unit,
            cost_min: +nv?.cost_min,
            cost_max: +nv?.cost_max,
            currency: nv?.currency || defaultCurrency || null,
            payment_type: nv?.payment_type,
            price_type: nv?.price_type,
            price_level: nv?.price_level,
            latitude: nv?.latitude,
            longitude: nv?.longitude,
        });

        if (!nv) {
            return;
        }
        //in case the selected activity have lat and lon
        if (nv.latitude && nv.latitude !== 0 && nv.longitude && nv.longitude !== 0) {
            // current_activity_id = nv.id
            let timeslot_activities = props.activities.filter((act) => act.id !== current_activity_id);

            for (let index = 0; index < timeslot_activities.length; index++) {
                let act = timeslot_activities[index].activity;
                if (!act.latitude && act.latitude === 0 && !act.longitude && act.longitude === 0) {
                    enqueueSnackbar('For the best user experience, we recommend that all activities in the same time slot either have or omit lat/lon coordinates', { variant: 'default', autoHideDuration: 5000 });
                    return;
                }
            }
        }

        //in case the selected activity does not have lat and lon
        if (!nv.latitude || nv.latitude === 0 || !nv.longitude || nv.longitude === 0) {
            // current_activity_id = nv.id
            let timeslot_activities = props.activities.filter((act) => act.id !== current_activity_id);
            for (let index = 0; index < timeslot_activities.length; index++) {
                let act = timeslot_activities[index].activity;
                if (act.latitude && act.latitude !== 0 && act.longitude && act.longitude !== 0) {
                    enqueueSnackbar('For the best user experience, we recommend that all activities in the same time slot either have or omit lat/lon coordinates', { variant: 'default', autoHideDuration: 5000 });
                    return;
                }
            }
        }
    };

    // const onCurrencyChange = (ev, nv) => {
    //     onChange({
    //         currency: nv.id
    //     })
    // }

    // const onPrepaidAddonCheck = (ev) => {
    //     onChange({
    //         payment_type: ev.target.checked ? PAYMENT_TYPE_PREPAID_ADDON : PAYMENT_TYPE_INCLUDED
    //     });
    // }

    // const onIntChange = (ev) => {
    //     onChange({
    //         [ev.target.name]: +ev.target.value
    //     });
    // }

    // const onFloatChange = (ev) => {
    //     onChange({
    //         [ev.target.name]: parseFloat(ev.target.value)
    //     });
    // }

    // const onTextChange = throttle((ev) => {
    //     onChange({
    //         [ev.target.name]: ev.target.value,
    //     });
    // }, 300);

    // const onSwitchChange = (ev) => {
    //     onChange({
    //         [ev.target.name]: ev.target.checked
    //     });
    // }

    const onPriceDisplayChange = (ev) => {
        // let new_object = { ...props.activity, ...parsePriceDisplay(+ev.target.value) }
        // if (+ev.target.value === 0 || +ev.target.value === 5) {
        //     new_object = { ...new_object, currency: null, cost_min: null, cost_max: null }
        //     onChange({
        //         ...new_object,
        //         ...parsePriceDisplay(+ev.target.value),
        //         price_display: +ev.target.value,
        //     });
        //     return;
        // }
        onChange({
            ...props.activity,
            ...parsePriceDisplay(+ev.target.value),
            price_display: +ev.target.value,
        });
    };

    // const onNextTransportChange = (ev, nv) => {
    //     props.onChange &&
    //         props.onChange(
    //             {
    //                 next_transport: nv,
    //             },
    //             props.idx
    //         );
    // };

    const onDelete = (ev) => {
        props.onDelete && props.onDelete(props.activity, props.idx);
        props.onValidate && props.onValidate(`activities[${props.idx}]`, null);
    };

    // const showPrices = true; // props.activity.payment_type === PAYMENT_TYPE_PAY_AT_LOCATION || props.activity.payment_type === PAYMENT_TYPE_PREPAID_ADDON;
    // const priceDisplay = guessPriceDisplay(props.activity);
    // const showPriceLevel = showPrices && (priceDisplay === PRICE_DISPLAY_PRICE_LEVEL);
    // const showCostMin = showPrices && ((priceDisplay === PRICE_DISPLAY_EXACT_PRICE) || (priceDisplay === PRICE_DISPLAY_PRICE_RANGE));
    // const showCostMax = showPrices && (priceDisplay === PRICE_DISPLAY_PRICE_RANGE);

    const { errors } = validateTripActivity(props.activity);

    const onOntripDocumentsChange = (documents) => {
        setDocs(documents);
        onChange({ documents });
    };
    const uploadOntripDocuments = (docs) => {
        if (!docs) {
            docs = docs || [];
        }
        enqueueSnackbar(`Uploading documents...`, { variant: 'default' });

        const fileList = docs.map((d, i) => ({ ...d, _idx: i })).filter((d) => !!d.file);
        const uploadOne = (file, idx) => {
            setSyncActive(idx);
            const params = {
                id: props.activity.id,
                data: {
                    file: file.file,
                    name: file.name,
                },
                urlSuffix: `${props.activity.id}/`,
                upload: true,
            };
            return dataAPI(UPLOAD, resource, params).then((res) => {
                if (res.statusCode === 200) {
                    setSyncActive(null);
                    if (docs.filter(doc => doc.id === res.data).length === 0) {
                        props.setNewDocsAdded(prev => {
                            if (!prev) {
                                return [props.activity.id]
                            }
                            return [...prev, props.activity.id]
                        })
                    }
                    onOntripDocumentsChange(docs.map((d, i) => (i !== idx ? d : res.data)));
                    enqueueSnackbar(`Document "${file.name}" uploaded successfully`, { variant: 'success' });
                } else {
                    enqueueSnackbar("Error uploading document '" + file.name + "': " + res.statusCode, { variant: 'error', autoHideDuration: 5000 });
                }
            });
        };
        const uploadNext = (file) => {
            uploadOne(file, file._idx).then((res) => {
                const next = fileList.shift();
                if (next) {
                    return uploadNext(next, next._idx);
                }
            });
        };
        uploadNext(fileList.shift());
    };

    const onDrop = React.useCallback(
        async (acceptedFiles) => {
            let hash_string = await fileToSha1String(acceptedFiles[0]);
            setDocs((prevState) => {
                if (prevState.filter((doc) => doc.content_hash === hash_string).length > 0) {
                    enqueueSnackbar('Duplicate file found, please check existing files', { variant: 'default', autoHideDuration: 3000 });
                    return prevState;
                }

                const newDocs = [...prevState, { file: acceptedFiles[0], name: acceptedFiles[0].name, content_hash: hash_string, type: FILE }];
                setTimeout(() => {
                    uploadOntripDocuments(newDocs);
                    onOntripDocumentsChange(newDocs);
                }, 0);
                return newDocs;
            });
            // eslint-disable-next-line react-hooks/exhaustive-deps
        },
        [props.activity]
    );

    // const processReplaceDocumentFile = async (file, sequence, name) => {
    //     let hash_string = await fileToSha1String(file);
    //     setDocs((prevState) => {
    //         if (prevState.filter((doc) => doc.content_hash === hash_string).length > 0) {
    //             enqueueSnackbar('Duplicate file found, please check existing files', { variant: 'default', autoHideDuration: 3000 });
    //             return prevState;
    //         }

    //         const newDocs = [...prevState, { file: file, name, sequence, content_hash: hash_string, type: FILE }];
    //         setTimeout(() => {
    //             uploadOntripDocuments(newDocs);
    //             onOntripDocumentsChange(newDocs);
    //         }, 0);
    //         return newDocs;
    //     });
    // };

    const dz = useDropzone({
        noClick: true,
        noKeyboard: true,
        maxFiles: 1,
        onDrop: onDrop,
        multiple: false,
    });

    const onAddLink = () => {
        setDocs((prevState) => {
            return [
                ...prevState.map((x) => ({ ...x, _is_open: false })),
                {
                    ...emptyLink,
                    sequence: Math.max(prevState.map((x) => +x.sequence)) + 1,
                    _is_open: true,
                },
            ];
        });
    };

    const onCopyActivityDocuments = () => {
        dataAPI(GET_LIST, 'activity_documents', { filter: { activity_id: props.activity.activity.id } }).then((res) => {
            if (res.statusCode === 200) {
                if (res.data.length <= 0) {
                    enqueueSnackbar('This activity has no on-trip documents', { variant: 'default' });
                } else {
                    const accepted = [];
                    setDocsToCopy(
                        res.data.map((x) => {
                            const isDuplicate = isDuplicateDocumentToCopy(x, docs, accepted);
                            if (!isDuplicate) {
                                accepted.push(x);
                            }
                            return { ...x, _selected: !isDuplicate, _duplicate: isDuplicate };
                        })
                    );
                }
            }
        });
    };

    const onDocsToCopyChange = (newdocs) => {
        setDocsToCopy(newdocs);
    };

    const onDocsToCopyClose = () => {
        setDocsToCopy(null);
    };

    const onDocsToCopySave = (replace) => {
        const selectedDocs = docsToCopy.filter((x) => !!x._selected);
        const docsToSave = filterDuplicateDocumentsToCopy(selectedDocs, docs, replace);

        if (docsToSave.length === 0) {
            enqueueSnackbar('Duplicate documents/links found, please check existing documents/links', {
                variant: 'default',
                autoHideDuration: 3000,
            });
            return;
        }

        if (docsToSave.length < selectedDocs.length) {
            enqueueSnackbar('Duplicate documents/links were skipped', { variant: 'default', autoHideDuration: 3000 });
        }

        const ids = docsToSave.map((x) => x.id);
        const body = { ids, replace: !!replace };
        setSyncActive(true);
        props.setNewDocsAdded(prev => {
            if (!prev) {
                return [props.activity.id]
            }
            return [...prev, props.activity.id]
        })
        dataAPI(UPDATE, resource, { id: props.activity.id, data: body, urlSuffix: 'copy_activity_documents/' })
            .then((res) => {
                if (res.statusCode === 200) {
                    onChange(res.data);
                    onDocsToCopyClose();
                    setDocs(res.data);
                    enqueueSnackbar('Documents/Links copied successfully', { variant: 'success' });
                } else {
                    enqueueSnackbar('Unexpected status code: ' + res.statusCode, { variant: 'error' });
                }
            })
            .finally(() => setSyncActive(false));
    };

    const onOntripDocumentDelete = (idx) => {
        props.setNewDocsAdded(prev => {
            if (!prev) {
                return [props.activity.id]
            }
            return [...prev, props.activity.id]
        })

        const doc = docs[idx];
        if (doc.id) {
            onOntripDocumentsChange((docs || []).map((d, i) => (i !== idx ? d : { ...d, uploading: true })));
            setSyncActive(true);
            dataAPI(DELETE, resource, {
                id: props.activity.id,
                data: { id: doc.id },
                urlSuffix: `${props.activity.id}/`,
            }).then((res) => {
                if (res.statusCode === 204) {
                    if (doc.type === 2) {
                        enqueueSnackbar('Link deleted', { variant: 'default' });
                    } else {
                        enqueueSnackbar('Document deleted', { variant: 'default' });
                    }

                    onOntripDocumentsChange((docs || []).filter((d, i) => i !== idx));
                } else {
                    enqueueSnackbar("Error deleting document '" + doc.name + "': " + res.statusCode, { variant: 'error', autoHideDuration: 5000 });
                }
                setSyncActive(false);
            });
        } else {
            onOntripDocumentsChange((docs || []).filter((d, i) => i !== idx));
        }
    };

    //DRAG AND DROP Functionality
    const dndref = React.useRef(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const [, drop] = useDrop({
        accept: DND_ITEM,
        // collect(monitor) {
        //     return {
        //         handlerId: monitor.getHandlerId(),
        //     };
        // },
        hover: (item, monitor) => {
            if (!dndref.current) {
                return;
            }
            const dragIndex = item.idx;
            const hoverIndex = props.idx;
            if (dragIndex === hoverIndex) {
                return;
            }
            const hoverBoundingRect = dndref.current?.getBoundingClientRect();
            // Get vertical middle
            const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
            // Determine mouse position
            const clientOffset = monitor.getClientOffset();
            // Get pixels to the top
            const hoverClientY = clientOffset.y - hoverBoundingRect.top;
            // Only perform the move when the mouse has crossed half of the items height
            // When dragging downwards, only move when the cursor is below 50%
            // When dragging upwards, only move when the cursor is above 50%
            // Dragging downwards
            if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
                return;
            }
            // Dragging upwards
            if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
                return;
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        drop: (item, monitor) => {
            const dragIndex = item.idx;
            const hoverIndex = props.idx;

            swapActivitySequence(dragIndex, hoverIndex);
        },
    });

    const [collected, drag, dragPreview] = useDrag({
        type: DND_ITEM,
        item: () => {
            return { id: props.activity.id, idx: props.idx };
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        // collect: (monitor, a) => {
        //     return {
        //         isDragging: monitor.isDragging(),
        //     };
        // },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        // end: (item, monitor) => {
        //     // props.saveOrder && props.saveOrder();
        // },
    });
    const opacity = collected.isDragging ? 0.5 : 1;

    drag(drop(dndref));

    const swapActivitySequence = (firstActivityIdx, secondActivityIdx) => {
        props.onChange(
            {
                sequence: secondActivityIdx,
            },
            firstActivityIdx
        );
        props.onChange(
            {
                sequence: firstActivityIdx,
            },
            secondActivityIdx
        );
    };

    React.useEffect(() => {
        setDocs(props.activity.documents);
    }, [props.activity]);

    const onChangeActivitySequence = (ActivityIdx, secondActivityIdx) => {
        if (ActivityIdx > secondActivityIdx) {
            //Swapping up
            if (ActivityIdx > 0) {
                props.onChange &&
                    props.onChange(
                        {
                            sequence: ActivityIdx - 1,
                        },
                        ActivityIdx
                    );
                props.onChange &&
                    props.onChange(
                        {
                            sequence: ActivityIdx,
                        },
                        secondActivityIdx
                    );
            }
        } else {
            //Swapping down
            if (ActivityIdx + 1 < props.activities.length) {
                props.onChange &&
                    props.onChange(
                        {
                            sequence: ActivityIdx + 1,
                        },
                        ActivityIdx
                    );
                props.onChange &&
                    props.onChange(
                        {
                            sequence: ActivityIdx,
                        },
                        secondActivityIdx
                    );
            }
        }
    };

    return (
        <div red={drop}>
            <Box ref={dragPreview} style={{ opacity: opacity }}>
                <ActivityDocumentsDialog documents={docsToCopy} open={!!docsToCopy} existingDocumentsCount={(docs || []).length} isSmallScreen={props.isSmallScreen} onChange={onDocsToCopyChange} onClose={onDocsToCopyClose} onSave={onDocsToCopySave} syncActive={syncActive} />
                <FixedLinearProgress visibility={syncActive} />
                <div className={classes.root}>
                    <Grid container direction="row" alignItems="center" spacing={props.isSmallScreen ? 1 : 2}>
                        {!props.disabled && props.activities.length > 1 && (
                            <Grid item style={{ width: props.isSmallScreen ? '55px' : '54px', paddingLeft: '1rem' }}>
                                <div style={{ display: 'flex', gap: '0.25rem', marginLeft: '-0.5rem' }}>
                                    <Tooltip title="Move up">
                                        <button className={classes.arrowButton} style={{ color: props.idx === 0 && grey[400], cursor: props.idx === 0 && 'default' }} onClick={() => onChangeActivitySequence(props.idx, props.idx - 1)}>
                                            <ArrowUpwardIcon style={{ fontSize: 16 }} />
                                        </button>
                                    </Tooltip>
                                    <Tooltip title="Move down">
                                        <button className={classes.arrowButton} style={{ color: props.idx === props.activities.length - 1 && grey[400], cursor: props.idx === props.activities.length - 1 && 'default' }} onClick={() => onChangeActivitySequence(props.idx, props.idx + 1)}>
                                            <ArrowDownwardIcon style={{ fontSize: 16 }} />
                                        </button>
                                    </Tooltip>
                                </div>
                            </Grid>
                        )}
                        {/* {!props.isSmallScreen && (
                            <Grid item xs={1} ref={dndref} className={classes.dragHandle}>
                                <DragIndicatorIcon color="primary" />
                            </Grid>
                        )} */}
                        {/* <Grid item xs={5} sm={4} ml={props.isSmallScreen ? 0 : -5}> */}
                        <Grid item xs={!props.activity.id ? 8 : 9} sm={9.5} ml={props.isSmallScreen ? 1.5 : 0} md={3} lg={4} xl={5}>
                            <StyledRemoteAutocomplete
                                disabled={props.disabled}
                                textFieldProps={{ ...errors.activity }}
                                resource="activity"
                                apiParams={{ ordering: '-rank' }}
                                urlSuffix={null}
                                label="Activity"
                                variant="filled"
                                size="small"
                                onChange={onActivityChange}
                                value={props.activity.activity || ''}
                                getOptionLabel={(opt) => {
                                    let activity_label = '';
                                    if (opt.name) {
                                        activity_label = `${opt.name} (${opt.id}): ${opt.address ? opt.address : opt.latitude?.toFixed(6) + ', ' + opt.longitude?.toFixed(6)}`;
                                    }
                                    return activity_label;
                                }}
                            />
                        </Grid>
                        {!props.activity.id && props.isSmallScreen && 
                            <Grid item xs={1}>
                                <DeleteIconButton title="Remove activity" fontSize="medium" onClick={onDelete} disabled={props.disabled} />
                            </Grid>
                        }
                        <Grid item xs={12} sm={3} style={{ paddingLeft: props.isSmallScreen ? '15px' : '0.5rem', paddingRight: props.isSmallScreen ? '10px' : 0 }}>
                            {props.activity?.activity?.id && (
                                <FormControl variant="filled" size="small" fullWidth disabled={props.disabled}>
                                    <InputLabel>Price Display</InputLabel>
                                    <Select {...errors.price_display} native name="price_display" value={priceDisplay} onChange={onPriceDisplayChange}>
                                        {priceDisplayHtmlOptions}
                                    </Select>
                                </FormControl>
                            )}
                        </Grid>
                        <Grid item xs={'auto'} container alignItems="center" justifyContent={'flex-start'} pr={props.isSmallScreen ? 0 : 2}>
                            <input {...dz.getInputProps()} />
                            {props.activity.id && (
                                <Tooltip title="Add document">
                                    <span>
                                        <IconButton variant="outlined" size="small" onClick={dz.open} disabled={!!syncActive || !props.activity.id || props.disabled}>
                                            <DescriptionIcon />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            )}
                            {props.activity.id && (
                                <Tooltip title="Add link">
                                    <span>
                                        <IconButton variant="outlined" size="small" onClick={onAddLink} disabled={!!syncActive || !props.activity.id || props.disabled}>
                                            <AddLinkIcon />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            )}

                            {props.activity.id && (
                                <Tooltip title="Copy from activity">
                                    <span>
                                        <IconButton variant="outlined" size="small" onClick={onCopyActivityDocuments} disabled={!!syncActive || !props.activity.id || props.disabled}>
                                            <FileCopyOutlinedIcon />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            )}
                            {props.activity?.activity && props.activity?.activity?.id ? (
                                <Tooltip title="Open activity in new tab">
                                    <IconButton size="small" href={`/activity/${props.activity?.activity?.id}`} target="_blank">
                                        <OpenInNewIcon />
                                    </IconButton>
                                </Tooltip>
                            ) : null}
                            {((props.activity.id && props.isSmallScreen) || (!props.activity.id && !props.isSmallScreen) || (props.activity.id && !props.isSmallScreen)) && (
                                <DeleteIconButton title="Remove activity" fontSize="medium" onClick={onDelete} disabled={props.disabled} />
                            )}
                        </Grid>
                        <Grid item xs={12} ml={!props.isSmallScreen && props.activities?.length > 1 ? '35px' : '-15px !important'} pr={props.isSmallScreen ? 0 : 6} container alignItems="center" spacing={1}>
                            <PriceDisplay disabled={props.disabled} priceDisplay={priceDisplay} object={props.activity} onChange={onChange} setClientErrors={props.setClientErrors} isSmallScreen={props.isSmallScreen} />
                        </Grid>
                        <Grid item xs={12} sm={12} lg={10} xl={9} ml={props.isSmallScreen && props.activities?.length ? '10px' : '0'} container alignItems="center" spacing={1}>
                            <CustomOntripDocumentList
                                disabled={props.disabled}
                                dz={dz}
                                docs={docs}
                                setDocs={setDocs}
                                activity={props.activity}
                                isSmallScreen={props.isSmallScreen}
                                onChange={onOntripDocumentsChange}
                                syncActive={syncActive}
                                setSyncActive={setSyncActive}
                                onOntripDocumentDelete={onOntripDocumentDelete}
                                setNewDocsAdded={props.setNewDocsAdded}
                            />
                        </Grid>
                    </Grid>
                    {!props.activity.id && (
                        <Grid item xs={12} pt={1} pl={props.isSmallScreen ? 0 : '50px'}>
                            <Typography>Save the trip to be able to upload documents to new activities</Typography>
                        </Grid>
                    )}
                    <Grid className={classes.divider}>
                        <Divider />
                    </Grid>
                </div>
            </Box >
        </div >
    );
};

export default TripFlowActivity;
